/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, extends as _extends } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { SkeletonText } from '@carbon/react';
import TagList from '../../Basic/TagList/TagList.js';
import { request } from '../../../Utilities/tagging/tagging.js';

var _excluded = ["resourceId", "reload", "onLoad", "mock", "supportAccessTags", "wrap"];
var usePrevious = function usePrevious(value) {
  var ref = useRef();
  useEffect(function () {
    ref.current = value; // assign the value of ref to the argument
  }, [value]); // this code will run when the value of 'value' changes
  return ref.current; // in the end, return the current ref value.
};
var ResourceTagList = function ResourceTagList(_ref) {
  var resourceId = _ref.resourceId,
    reload = _ref.reload,
    onLoad = _ref.onLoad,
    mock = _ref.mock,
    supportAccessTags = _ref.supportAccessTags,
    wrap = _ref.wrap,
    other = _objectWithoutProperties(_ref, _excluded);
  var _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    tags = _useState2[0],
    setTags = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    fetchingTags = _useState4[0],
    setFetchingTags = _useState4[1];
  var fetchAttachedTags = useCallback(function () {
    var url = "/account/api/tagging/resource?id=".concat(resourceId);
    return new Promise(function (resolve) {
      request({
        url: url
      }).then(function (resp) {
        var _resp$tags = resp.tags,
          localTags = _resp$tags === void 0 ? [] : _resp$tags;
        var _ref2 = supportAccessTags ? resp : {
            access_tags: []
          },
          _ref2$access_tags = _ref2.access_tags,
          accessTags = _ref2$access_tags === void 0 ? [] : _ref2$access_tags;
        var success = resp.success,
          error = resp.error;
        resolve({
          success: success,
          error: error,
          tags: localTags,
          accessTags: accessTags
        });
      });
    });
  }, [resourceId, supportAccessTags]);
  /**
   * Makes a request to the tagging endpoint to return the tags attached to the given CRN.
   */
  var queryAttachedTags = useCallback(function () {
    setFetchingTags(true);
    return fetchAttachedTags().then(function (_ref3) {
      var success = _ref3.success,
        error = _ref3.error,
        _ref3$tags = _ref3.tags,
        localTags = _ref3$tags === void 0 ? [] : _ref3$tags,
        _ref3$accessTags = _ref3.accessTags,
        accessTags = _ref3$accessTags === void 0 ? [] : _ref3$accessTags;
      if (success) {
        var userTags = localTags.map(function (tag) {
          return {
            name: tag
          };
        });
        var allTags = userTags.concat(accessTags.map(function (tag) {
          return {
            name: tag,
            isAccessTag: true
          };
        }));
        return {
          tags: allTags,
          success: success,
          error: error
        };
      }
      return {
        tags: [],
        success: success,
        error: error
      };
    })["finally"](function () {
      setFetchingTags(false);
    });
  }, [fetchAttachedTags]);
  useEffect(function () {
    if (mock) {
      setTags([{
        name: 'tag-1',
        type: 'functional'
      }, {
        name: 'tag-2',
        type: 'functional'
      }, {
        name: 'tag-3',
        type: 'functional',
        isAccessTag: true
      }, {
        name: 'tag-4',
        type: 'functional',
        isAccessTag: true
      }]);
    } else {
      window.header.whenAccountReady(function () {
        queryAttachedTags().then(function (_ref4) {
          var success = _ref4.success,
            error = _ref4.error,
            localTags = _ref4.tags;
          onLoad({
            success: success,
            error: error,
            tags: localTags
          });
          setTags(localTags);
        });
      });
    }
  }, [mock, onLoad, queryAttachedTags]);
  var prevReload = usePrevious(reload);
  useEffect(function () {
    if (prevReload === false && reload === true) {
      queryAttachedTags().then(function (_ref5) {
        var success = _ref5.success,
          error = _ref5.error,
          localTags = _ref5.tags;
        onLoad({
          success: success,
          error: error,
          tags: localTags
        });
        setTags(localTags);
      });
    }
  }, [onLoad, prevReload, queryAttachedTags, reload]);
  return /*#__PURE__*/React.createElement("div", {
    className: "cpc--resource-tag-list"
  }, tags.length === 0 && fetchingTags && /*#__PURE__*/React.createElement(SkeletonText, {
    heading: true
  }), (tags.length > 0 || tags.length === 0 && !fetchingTags) &&
  /*#__PURE__*/
  // eslint-disable-next-line react/jsx-props-no-spreading
  React.createElement(TagList, _extends({
    tags: tags,
    wrap: wrap,
    maxCharacters: 13
  }, other)));
};
ResourceTagList.propTypes = {
  /**
   * The id (Softlayer) or crn (Cloud Resource Name) used to populate the TagList container with tags currently attached to the resource.
   */
  resourceId: PropTypes.string.isRequired,
  /**
   * Whether or not the tags should be reloaded.
   */
  reload: PropTypes.bool,
  /**
   * A function that is called once all of the attached tags have been loaded.
   */
  onLoad: PropTypes.func,
  /**
   * Whether or not to mock the requests
   */
  mock: PropTypes.bool,
  /**
   * Whether or not to display access tags
   */
  supportAccessTags: PropTypes.bool,
  /**
   * Whether or not the tag list should wrap when they reach the width of their container.
   */
  wrap: PropTypes.bool
};
ResourceTagList.defaultProps = {
  reload: false,
  onLoad: function onLoad() {},
  mock: false,
  supportAccessTags: false,
  wrap: false
};
var ResourceTagList$1 = ResourceTagList;

export { ResourceTagList$1 as default };
